import React, { useMemo } from 'react';
import { useRole } from '../../context/roleContext';
import {
  faFileExport,
  faTrash,
  faRefresh,
  faFileImport,
  faAdd
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS } from '../../utils/colors';
import ActionToolbar from '../common/ActionToolbar';
import { useTranslation } from 'react-i18next';

/**
 * ProductActions Component
 * Action buttons for product management (bulk actions, export, import, etc.)
 */
const ProductActions = ({
  selectedProducts = [],
  products = [],
  onAddProduct,
  onDeleteSelected,
  onRefresh,
  onImport,
  onExport,
  onDownloadTemplate,
  showSearch = true,
  searchValue = '',
  onSearchChange,
  loading = false,
  refreshLoading = false,
  importing = false,
  exporting = false
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess } = useRole();

  /**
   * Build the toolbar actions array.
   */
  const actions = useMemo(() => {
    const items = [];

    if (selectedProducts.length > 0 && checkDeleteAccess()) {
      items.push({
        key: 'delete-selected',
        label: t('common.buttons.deleteSelected'),
        icon: faTrash,
        onClick: onDeleteSelected,
        disabled: loading || importing || exporting,
        variant: 'danger',
        showLabelBreakpoint: 'sm',
      });
    }

    items.push(
      {
        key: 'import-products',
        label: t('productCatalogue.actions.buttons.import'),
        icon: faFileImport,
        onClick: onImport,
        disabled: loading || importing || exporting,
        variant: 'secondary',
        showLabelBreakpoint: 'sm',
        spinner: importing,
      },
      {
        key: 'export-products',
        label: t('common.buttons.exportCsv'),
        icon: faFileExport,

        onClick: onExport,
        disabled: loading || importing || exporting,
        variant: 'secondary',
        showLabelBreakpoint: 'sm',
        textColor: UI_COLORS.TEXT.WHITE,
      },
      {
        key: 'refresh-products',
        label: t('common.buttons.refresh'),
        icon: faRefresh,
        onClick: onRefresh,
        disabled: refreshLoading || importing || exporting,
        variant: 'ghost',
        showLabelBreakpoint: 'sm',
        spinner: refreshLoading,
      },
      {
        key: 'add-product',
        label: t('productCatalogue.actions.buttons.add'),
        icon: faAdd,
        onClick: onAddProduct,
        disabled: loading || importing || exporting,
        variant: 'primary',
        showLabelBreakpoint: 'sm',
        loading,
      }
    );

    return items;
  }, [
    onExport,
    loading,
    onAddProduct,
    onDeleteSelected,
    onRefresh,
    onImport,
    refreshLoading,
    selectedProducts.length,
    importing,
    exporting,
    checkDeleteAccess,
    t
  ]);

  return (
    <ActionToolbar
      showSearch={showSearch}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('productCatalogue.actions.searchPlaceholder')}
      actions={actions}
    />
  );
};

export default ProductActions;
